import React from "react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { tokens } from "../../public/tokens";
import { CHART_COLORS, CHART_MARGIN, CHART_TEXT_COLOR, ChartBody, ChartFrame, ChartTitle } from "./Chart";

const { colors, typography } = tokens;

const GRID_COLOR = "#D9D9D9";
const AXIS_TICK = { fill: CHART_TEXT_COLOR, fontSize: typography.fontSize["md-12px"] };
/** Recharts gives all-zero data a 0-4 scale; this pins it to a single 0 baseline. */
const ZERO_DATA_Y_AXIS = { domain: [0, 1], ticks: [0] };
const TITLED_AXES_MARGIN = { top: 10, right: 5, bottom: 30, left: 30 };

const AXIS_TITLE_STYLE = {
    fill: colors.neutral.black,
    fontSize: typography.fontSize["lg-14px"],
    fontWeight: typography.fontWeights.semibold,
};

/**
 * Single-series column chart with optional chart and axis titles.
 * @param {object[]} data One row per column
 * @param {string} categoryKey Row field shown along the x-axis
 * @param {string} valueKey Row field giving each column's height; also the tooltip label
 * @param {number} height Chart height in px, including the title
 * @param {string} [title] Heading above the chart
 * @param {string} [xAxisTitle] Label under the x-axis
 * @param {string} [yAxisTitle] Label beside the y-axis
 */
export function ColumnChart({ data, categoryKey, valueKey, height, title, xAxisTitle, yAxisTitle }) {
    const hasOnlyZeros = data.every((row) => Number(row[valueKey]) === 0);
    const hasAxisTitles = Boolean(xAxisTitle || yAxisTitle);

    return (
        <ChartFrame $height={height}>
            {title && <ChartTitle>{title}</ChartTitle>}
            <ChartBody>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data} margin={hasAxisTitles ? TITLED_AXES_MARGIN : CHART_MARGIN} barCategoryGap="25%">
                        <CartesianGrid vertical={false} stroke={GRID_COLOR} syncWithTicks />
                        <XAxis
                            dataKey={categoryKey}
                            tick={AXIS_TICK}
                            stroke="#BFBFBF"
                            tickLine={false}
                            label={xAxisTitle && { value: xAxisTitle, position: "bottom", offset: 10, ...AXIS_TITLE_STYLE }}
                        />
                        <YAxis
                            width={40}
                            tick={AXIS_TICK}
                            axisLine={false}
                            tickLine={false}
                            {...(hasOnlyZeros && ZERO_DATA_Y_AXIS)}
                            label={yAxisTitle && { value: yAxisTitle, angle: -90, position: "left", textAnchor: "middle", ...AXIS_TITLE_STYLE }}
                        />
                        <Tooltip cursor={{ fill: GRID_COLOR, fillOpacity: 0.25 }} />
                        <Bar dataKey={valueKey} fill={CHART_COLORS[0]} />
                    </BarChart>
                </ResponsiveContainer>
            </ChartBody>
        </ChartFrame>
    );
}
